import { FunctionComponent, useCallback, useMemo } from 'react'
import Icon from '@/components/RebrandedHeroIcons'
import { ProfessionalAvatar } from '../../../../components/ProfessionalAvatar'
import { useStyles } from './styles'
import { ConsultationCardProps, GoToConsultationDetailsParams } from './types'
import {
  getDateInFull,
  getDayPeriod,
  getTimeInHourAndMinute,
  convertArrayToDateTime,
} from '@/utils/date'
import {
  Text,
  Tag,
  Button,
  Box,
  toast,
  getTheme,
} from '@conexasaude/conexa-hero'
import { RenderIf } from '@/components/RenderIf'
import { useHistory } from 'react-router-dom'
import {
  confirmGroupConsultation,
  getGroupConsultationURL,
} from '../../my-calls-services'
import { requestCallTokenGroup } from '@/modules/RealtimeAttendance/services'
import { openExternalLink, returnFormattedDate } from '@/utils/fnUtils'
import { useTokenAmil } from '../../store/useTokenAmil'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { useModalContext } from '@/components/RebrandedModal/contexts/ModalContext'
import { DateTime } from 'luxon'

const ConsultationGroupCard: FunctionComponent<ConsultationCardProps> = ({
  professionalId,
  professionalName,
  professionalPicture,
  specialty,
  isCancelled,
  readOnly,
  theme: consultationTheme,
  bodyOnly,
  onlyConfirmation,
  noTag,
  id,
  status,
  appointmentDate,
  patientConfirmation,
}) => {
  const theme = getTheme()
  const history = useHistory()
  const styles = useStyles({ isCancelled })
  const setGuideNumber = useTokenAmil(state => state.setGuideNumber)
  const { clinic, user } = useAuthContext()
  const { showModal } = useModalContext()

  const canEnterConsultation = useMemo(() => {
    const now = DateTime.now()
    const remainMillis =
      now.toMillis() - convertArrayToDateTime(appointmentDate).toMillis()
    return remainMillis >= 0
  }, [appointmentDate])

  const handleAttendanceOpenClickExternOperator = useCallback(async () => {
    const consultationGroupUrl = await getGroupConsultationURL(String(id))

    if (clinic?.flag?.LiberarChamadaPorTokenAutorizacaoExterno) {
      try {
        const response = await requestCallTokenGroup(
          String(id),
          String(professionalId),
          returnFormattedDate(),
        )
        if (response?.validatedToken) {
          history.push('/chamada-grupo', consultationGroupUrl)
        } else {
          setGuideNumber(response?.providerGuideNumber)
          showModal('CALL_STARTED_WITH_TOKEN_GROUP', {
            title: `Informe o token de segurança da ${
              clinic?.friendlyName || user?.clinic?.name
            }`,
            persistent: true,
          })
        }
      } catch (err) {
        toast({
          message: err?.response?.data?.msg,
          variant: 'error',
          autoClose: false,
          position: 'top-right',
        })
      }
    }
  }, [
    id,
    clinic?.flag?.LiberarChamadaPorTokenAutorizacaoExterno,
    clinic?.friendlyName,
    professionalId,
    history,
    setGuideNumber,
    showModal,
    user?.clinic?.name,
  ])

  const handleAttendanceOpenClick = useCallback(async () => {
    try {
      const groupConsultationResponse = await getGroupConsultationURL(
        String(id),
      )

      if (groupConsultationResponse?.meetingId) {
        openExternalLink(groupConsultationResponse?.meetingUrl)
      } else {
        toast({
          message:
            'O Profissional não se encontra na consulta, tente novamente mais tarde',
          variant: 'error',
          autoClose: false,
          position: 'top-right',
        })
      }
    } catch (err) {
      history.push('/')

      toast({
        message: err?.response?.data?.msg,
        variant: 'error',
        autoClose: false,
        position: 'top-right',
      })
    }
  }, [history, id])

  const handleEnterConsultation = useCallback(async () => {
    if (clinic?.flag.LiberarChamadaPorTokenAutorizacaoExterno) {
      await handleAttendanceOpenClickExternOperator()
      return
    }

    await handleAttendanceOpenClick()
  }, [
    clinic?.flag.LiberarChamadaPorTokenAutorizacaoExterno,
    handleAttendanceOpenClick,
    handleAttendanceOpenClickExternOperator,
  ])

  const goToConsultationDetails = async ({
    id,
  }: GoToConsultationDetailsParams) => {
    history.push(`/meus-atendimentos/atendimento/grupo/${id}`)
  }

  const handleCardClick = () => {
    if (readOnly) {
      return
    }
    goToConsultationDetails({ id })
  }

  const handleConfirmClick = async () => {
    try {
      await confirmGroupConsultation(id.toString(), 'CONFIRMADO')
      history.push(`/meus-atendimentos/atendimento/grupo/confirmado`)
    } catch (error) {
      const errorMessage =
        error?.response?.data?.msg || error?.response?.msg || error?.message

      toast({
        message: errorMessage,
        variant: 'error',
      })
    }
  }

  const handleRecuseClick = async () => {
    showModal('CANCEL_OR_RESCHEDULE_CONSULTATION', {
      title: 'Você deseja cancelar sua participação na consulta em grupo?',
    })
  }

  const dateInFull = getDateInFull(appointmentDate, {
    reversed: true,
  })
  const timeInHourAndMinute = getTimeInHourAndMinute(appointmentDate)
  const dayPeriod = getDayPeriod(appointmentDate)

  const convertStatus = (patientConfirmation: string) => {
    if (patientConfirmation === 'CONFIRMADO') {
      return 'Confirmada'
    }
    if (patientConfirmation === 'RECUSADO') {
      return 'Recusada'
    }
    return 'Aguardando confirmação'
  }

  const getTagType = (patientConfirmation: string, status: string) => {
    if (patientConfirmation === 'CONFIRMADO' || status === 'Concluído') {
      return 'success'
    }

    if (patientConfirmation === 'RECUSADO' || status === 'Cancelado') {
      return 'error'
    }

    return 'information'
  }

  return (
    <Box onClick={handleCardClick}>
      <RenderIf condition={!bodyOnly}>
        <ProfessionalAvatar
          picture={professionalPicture}
          name={professionalName}
          speciality={specialty}
        />
      </RenderIf>

      <RenderIf condition={bodyOnly}>
        <Text renderAs="h5" variant="headingSmall">
          Dia e horário consulta em grupo
        </Text>
      </RenderIf>

      <div css={styles.cardConsultationInfo}>
        <RenderIf
          condition={
            patientConfirmation !== 'CONFIRMADO' &&
            status !== 'Cancelado' &&
            status !== 'Concluído' &&
            !noTag &&
            !bodyOnly
          }
        >
          <Tag variant={getTagType(patientConfirmation, status)}>
            {convertStatus(patientConfirmation)}
          </Tag>
        </RenderIf>

        <RenderIf condition={status === 'Cancelado' && !bodyOnly && !noTag}>
          <Tag variant={getTagType(patientConfirmation, status)}>Cancelado</Tag>
        </RenderIf>

        <RenderIf condition={status === 'Concluído' && !bodyOnly && !noTag}>
          <Tag variant={getTagType(patientConfirmation, status)}>Concluído</Tag>
        </RenderIf>

        <div>
          <Text variant="headingSmall">Consulta em grupo</Text>
        </div>

        <div css={styles.base}>
          <Icon
            name="medical-notes"
            size="24"
            color={theme.colors.neutral[isCancelled ? 500 : 200]}
          />
          <Text>{consultationTheme}</Text>
        </div>

        <div css={styles.base}>
          <Icon
            name="calendar"
            size="24"
            color={theme.colors.neutral[isCancelled ? 500 : 200]}
          />
          <Text>{dateInFull}</Text>
        </div>

        <div css={styles.base}>
          <Icon
            name="clock"
            size="24"
            color={theme.colors.neutral[isCancelled ? 500 : 200]}
          />
          <Text>
            {timeInHourAndMinute} ({dayPeriod})
          </Text>
        </div>
      </div>

      <RenderIf condition={patientConfirmation === 'NAO_RESPONDEU'}>
        <div css={styles.cardFooter}>
          <RenderIf condition={!onlyConfirmation}>
            <Button variant="tertiaryDanger" onClick={handleRecuseClick}>
              Cancelar
            </Button>
          </RenderIf>
          <Button onClick={handleConfirmClick}>Confirmar</Button>
        </div>
      </RenderIf>

      <RenderIf condition={patientConfirmation === 'CONFIRMADO'}>
        <div css={styles.cardFooter}>
          <Button
            id="enter-button"
            onClick={handleEnterConsultation}
            disabled={!canEnterConsultation}
          >
            Entrar
          </Button>
        </div>
      </RenderIf>
    </Box>
  )
}

export type { ConsultationCardProps }
export { ConsultationGroupCard }
